import PropTypes from 'prop-types';

const UploadProgressModal = ({
    isVisible,
    progress,
    totalFiles,
    totalSize,
    uploadedSize,
    onCancel,
    isUploading
}) => {
    if (!isVisible) return null;

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
    };

    const getStatusText = () => {
        if (!isUploading && progress === 0) return 'Upload cancelled';
        if (progress === 0) return 'Preparing upload...';
        if (progress === 100) return 'Upload completed!';
        return 'Transferring...';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 w-96 flex flex-col items-center">
                <div className="relative w-32 h-32 mb-4">
                    {progress === 100 ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <i className="fa-solid fa-circle-check text-6xl text-green-500"></i>
                        </div>
                    ) : (
                        <>
                            <div className="absolute inset-0 rounded-full bg-blue-100 animate-pulse"
                                style={{ opacity: progress < 100 ? 0.5 : 0 }}
                            />
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                <circle
                                    className="text-gray-200"
                                    strokeWidth="8"
                                    stroke="currentColor"
                                    fill="transparent"
                                    r="42"
                                    cx="50"
                                    cy="50"
                                />
                                <circle
                                    className="text-gray-900 transition-all duration-300"
                                    strokeWidth="8"
                                    strokeDasharray={264}
                                    strokeDashoffset={264 - (264 * progress) / 100}
                                    strokeLinecap="round"
                                    stroke="currentColor"
                                    fill="transparent"
                                    r="42"
                                    cx="50"
                                    cy="50"
                                />
                            </svg>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                                <span className="text-3xl font-bold">{Math.round(progress)}%</span>
                            </div>
                        </>
                    )}
                </div>

                <div className="text-center mb-6 w-full">
                    <p className={`font-medium text-lg mb-2 ${progress === 100 ? 'text-green-500' : ''
                        }`}>
                        {getStatusText()}
                    </p>
                    <p className="text-sm text-blue-600 mb-2">
                        {progress === 100
                            ? `Successfully uploaded ${totalFiles} ${totalFiles > 1 ? 'files' : 'file'}`
                            : `Uploading ${totalFiles} ${totalFiles > 1 ? 'files' : 'file'} to server`
                        }
                    </p>
                    <div className="w-full bg-gray-200 h-1.5 rounded-full mb-2">
                        <div
                            className={`h-1.5 rounded-full transition-all duration-300 ${progress === 100 ? 'bg-green-500' : 'bg-black'
                                }`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <p className="text-sm text-gray-600">
                        {formatFileSize(uploadedSize)} of {formatFileSize(totalSize)}
                    </p>
                </div>

                {isUploading && progress < 100 && (
                    <button
                        onClick={onCancel}
                        className="px-8 py-2 text-blue-600 border border-blue-600 rounded-lg
                       hover:bg-blue-50 active:bg-blue-100 
                       transition-colors duration-200
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                    >
                        Cancel Upload
                    </button>
                )}
            </div>
        </div>
    );
};
UploadProgressModal.propTypes = {
    totalFiles: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    isVisible: PropTypes.bool.isRequired,
    progress: PropTypes.number.isRequired,
    totalSize: PropTypes.number.isRequired,
    uploadedSize: PropTypes.number.isRequired,
    isUploading: PropTypes.bool.isRequired
};

export default UploadProgressModal;
